// Página de diagnóstico do FORMINI: mostra exatamente o que está rodando
// no dispositivo (versão do código, última sync, multiplicadores carregados).
//
// Caso de uso: vendedora reclama que o preço do FORMINI no celular está
// diferente do desktop. Ela abre essa página nos 2 dispositivos, manda
// 2 prints, comparamos lado a lado e descobrimos o que difere.

import { useState, useEffect } from 'react'
import { getPrecos } from '../utils/precos'
import { syncFromSupabase } from '../utils/supabase'
import { RefreshCw, Trash2, AlertCircle, CheckCircle2, Copy } from 'lucide-react'

// Hash injetado pelo Vite no build — permite ver se 2 dispositivos
// estão rodando a mesma versão do JS.
const BUILD_TIME = new Date().toISOString().split('T')[0] // só pra ter algo

function fmtDateTime(iso) {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'medium' })
  } catch { return iso }
}

function deltaTempo(iso) {
  if (!iso) return null
  try {
    const segs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
    if (segs < 60)   return `há ${segs}s`
    if (segs < 3600) return `há ${Math.floor(segs / 60)} min`
    if (segs < 86400) return `há ${Math.floor(segs / 3600)}h`
    return `há ${Math.floor(segs / 86400)}d`
  } catch { return null }
}

export function DiagnosticoFormini() {
  const [precos, setPrecos] = useState(() => getPrecos())
  const [lastSync, setLastSync] = useState(() => localStorage.getItem('mini_last_sync_at'))
  const [lastErr, setLastErr]   = useState(() => localStorage.getItem('mini_last_sync_error'))
  const [lastCount, setLastCount] = useState(() => localStorage.getItem('mini_last_sync_count'))
  const [sincronizando, setSincronizando] = useState(false)
  const [resultado, setResultado] = useState(null)
  // Re-renderiza o "há X min" periodicamente
  const [, setTick] = useState(0)
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 10_000)
    return () => clearInterval(t)
  }, [])

  async function sincronizar() {
    if (sincronizando) return
    setSincronizando(true)
    setResultado(null)
    localStorage.removeItem('mini_last_sync_error')
    try {
      const ok = await syncFromSupabase()
      setLastSync(localStorage.getItem('mini_last_sync_at'))
      setLastErr(localStorage.getItem('mini_last_sync_error'))
      setLastCount(localStorage.getItem('mini_last_sync_count'))
      setPrecos(getPrecos())
      if (ok) {
        window.dispatchEvent(new CustomEvent('mini:resynced', { detail: { motivo: 'diagnostico' } }))
        setResultado({ ok: true, msg: 'Re-sincronizado com sucesso. Preços abaixo já estão atualizados.' })
      } else {
        setResultado({ ok: false, msg: 'Falhou. Veja a mensagem de erro abaixo.' })
      }
    } finally {
      setSincronizando(false)
    }
  }

  function limparCacheLocal() {
    if (!window.confirm('Apaga a tabela de preços local e re-sincroniza. Continua?')) return
    localStorage.removeItem('mini_tabela_precos')
    sincronizar()
  }

  async function copiarDiagnostico() {
    const txt = [
      `=== DIAGNÓSTICO FORMINI ===`,
      `Data: ${new Date().toLocaleString('pt-BR')}`,
      `Build de referência: ${BUILD_TIME}`,
      `User Agent: ${navigator.userAgent}`,
      `Online: ${navigator.onLine}`,
      ``,
      `--- ÚLTIMO SYNC ---`,
      `Quando: ${fmtDateTime(lastSync)} (${deltaTempo(lastSync) || '—'})`,
      `Chaves recebidas: ${lastCount || '—'}`,
      `Erro: ${lastErr || 'nenhum'}`,
      ``,
      `--- MULTIPLICADOR DEFAULT ---`,
      `${precos.multiplicadorDefault}`,
      ``,
      `--- MULTIPLICADORES POR MODELO ---`,
      Object.entries(precos.multiplicadores || {}).length === 0
        ? '(vazio — todos os modelos usam o default)'
        : Object.entries(precos.multiplicadores || {})
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([modelo, mult]) => `${modelo}: ${mult}`).join('\n'),
      ``,
      `--- PREÇOS POR MODELO (cadastrados manual) ---`,
      Object.entries(precos.modelos || {}).length === 0
        ? '(vazio)'
        : Object.entries(precos.modelos || {})
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([modelo, p]) => `${modelo}: R$ ${p}`).join('\n'),
      ``,
      `--- SURCHARGE POR COR ---`,
      JSON.stringify(precos.surchargeCores || {}, null, 2),
    ].join('\n')
    try {
      await navigator.clipboard.writeText(txt)
      alert('Diagnóstico copiado — cole no chat')
    } catch {
      window.prompt('Copie manualmente:', txt)
    }
  }

  const multsCustom = Object.entries(precos.multiplicadores || {})
    .sort(([a], [b]) => a.localeCompare(b))

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <h1 className="text-xl font-bold text-slate-800 mb-1">Diagnóstico do FORMINI</h1>
      <p className="text-sm text-slate-500 mb-5">
        Abra essa página no celular E no desktop, manda 2 prints pro chat.
        Vou comparar lado a lado e identificar o que está diferente.
      </p>

      {/* Última sincronização */}
      <div className="card p-5 mb-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h2 className="text-sm font-bold text-slate-700">Última sincronização com Supabase</h2>
            <p className="text-xs text-slate-400 mt-0.5">É daqui que vêm os preços. Se essa data tá muito antiga, os preços do app podem estar defasados.</p>
          </div>
          <button
            onClick={sincronizar}
            disabled={sincronizando}
            className="btn-primary text-sm shrink-0 disabled:opacity-50"
          >
            <RefreshCw size={14} className={sincronizando ? 'animate-spin' : ''} />
            {sincronizando ? 'Sincronizando…' : 'Sincronizar agora'}
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wide font-semibold mb-1">Quando</p>
            <p className="font-mono text-slate-700">
              {fmtDateTime(lastSync)}
              {deltaTempo(lastSync) && (
                <span className="ml-2 text-xs text-slate-400">({deltaTempo(lastSync)})</span>
              )}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wide font-semibold mb-1">Chaves recebidas</p>
            <p className="font-mono text-slate-700">{lastCount || '—'}</p>
          </div>
        </div>

        {lastErr && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle size={14} className="text-red-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-red-700">Último erro de sync:</p>
              <p className="text-xs font-mono text-red-600 mt-0.5 break-all">{lastErr}</p>
            </div>
          </div>
        )}

        {resultado && (
          <div className={`mt-3 p-3 border rounded-lg flex items-start gap-2 ${
            resultado.ok ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
          }`}>
            {resultado.ok
              ? <CheckCircle2 size={14} className="text-green-600 shrink-0 mt-0.5" />
              : <AlertCircle size={14} className="text-red-600 shrink-0 mt-0.5" />}
            <p className={`text-xs ${resultado.ok ? 'text-green-700' : 'text-red-700'}`}>
              {resultado.msg}
            </p>
          </div>
        )}
      </div>

      {/* Configuração de preços */}
      <div className="card p-5 mb-4">
        <h2 className="text-sm font-bold text-slate-700 mb-3">Configuração de preços (em uso AGORA)</h2>

        <div className="mb-4">
          <p className="text-xs text-slate-400 uppercase tracking-wide font-semibold mb-1">
            Multiplicador default
          </p>
          <p className="text-2xl font-black text-[#CC8800] font-mono">
            {precos.multiplicadorDefault}
            <span className="text-sm text-slate-400 font-normal ml-2">
              (aplica em todo modelo sem multiplicador próprio)
            </span>
          </p>
        </div>

        <div>
          <p className="text-xs text-slate-400 uppercase tracking-wide font-semibold mb-2">
            Multiplicadores individuais ({multsCustom.length})
          </p>
          {multsCustom.length === 0 ? (
            <p className="text-sm text-slate-400 italic">
              Nenhum modelo com multiplicador individual — todos usam o default.
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-2 text-sm">
              {multsCustom.map(([modelo, mult]) => (
                <div key={modelo} className="flex items-center justify-between gap-2 p-2 rounded-lg bg-slate-50 border border-slate-200">
                  <span className="text-slate-700 truncate" title={modelo}>{modelo}</span>
                  <span className="font-bold font-mono text-[#CC8800] shrink-0">{mult}×</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {Object.keys(precos.surchargeCores || {}).some(k => precos.surchargeCores[k] !== 0) && (
          <div className="mt-4 pt-3 border-t border-slate-200">
            <p className="text-xs text-slate-400 uppercase tracking-wide font-semibold mb-2">
              Surcharge por cor (na precificação manual)
            </p>
            <div className="grid grid-cols-3 gap-2 text-sm">
              {Object.entries(precos.surchargeCores || {}).map(([cor, pct]) => (
                <div key={cor} className="text-center p-2 rounded-lg bg-slate-50">
                  <p className="text-xs text-slate-500">{cor}</p>
                  <p className="font-bold font-mono">{pct > 0 ? '+' : ''}{pct}%</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Info do dispositivo */}
      <div className="card p-5 mb-4">
        <h2 className="text-sm font-bold text-slate-700 mb-2">Dispositivo</h2>
        <div className="space-y-1 text-xs text-slate-500 font-mono break-all">
          <p>Online: {String(navigator.onLine)}</p>
          <p>URL: {window.location.href}</p>
          <p>User-Agent: {navigator.userAgent}</p>
        </div>
      </div>

      {/* Ações */}
      <div className="flex flex-wrap gap-2">
        <button onClick={copiarDiagnostico} className="btn-secondary text-sm">
          <Copy size={14} />
          Copiar diagnóstico
        </button>
        <button onClick={limparCacheLocal} className="btn-danger text-sm">
          <Trash2 size={14} />
          Limpar cache + re-sincronizar
        </button>
      </div>

      <p className="text-[11px] text-slate-400 mt-4 leading-relaxed">
        Como diagnosticar diferença entre celular e desktop: abra essa página
        nos 2, clique em <strong>"Copiar diagnóstico"</strong> em cada um, e
        cole os 2 textos no chat. Eu identifico a diferença e arrumo.
      </p>
    </div>
  )
}
